import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIsMobile } from '@/hooks/use-mobile';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { ArrowLeft, ArrowRight, Check } from 'lucide-react';
import { toast } from 'sonner';
import { offlineDB } from '@/lib/offlineDB';
import { nanoid } from 'nanoid';
import { JobLeadSelector } from '@/components/inspection/JobLeadSelector';
import { QuickInspectionForm } from '@/components/inspection/QuickInspectionForm';
import { PhotoCaptureStep } from '@/components/inspection/PhotoCaptureStep';

type Step = 'select' | 'form' | 'photos';

export default function MobileInspectionFlow() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [currentStep, setCurrentStep] = useState<Step>('select');
  const [selectedJob, setSelectedJob] = useState<string | null>(null);
  const [selectedLead, setSelectedLead] = useState<string | null>(null);
  const [formData, setFormData] = useState<any>({});
  const [photos, setPhotos] = useState<any[]>([]);

  const steps: Step[] = ['select', 'form', 'photos'];
  const currentStepIndex = steps.indexOf(currentStep);
  const progress = ((currentStepIndex + 1) / steps.length) * 100;

  if (!isMobile) {
    return (
      <div className="flex items-center justify-center min-h-screen p-4">
        <Card className="p-6 max-w-md text-center">
          <h2 className="text-xl font-semibold mb-2">Mobile Only</h2>
          <p className="text-muted-foreground">
            This inspection flow is optimized for mobile devices.
            Please access from a mobile device or use the standard inspection tools.
          </p>
          <Button onClick={() => navigate('/jobs')} className="mt-4">
            Go to Jobs
          </Button>
        </Card>
      </div>
    );
  }

  const handleNext = () => {
    if (currentStep === 'select') {
      if (!selectedJob && !selectedLead) {
        toast.error('Please select a job or lead');
        return;
      }
      setCurrentStep('form');
    } else if (currentStep === 'form') {
      setCurrentStep('photos');
    }
  };

  const handleBack = () => {
    if (currentStep === 'form') {
      setCurrentStep('select');
    } else if (currentStep === 'photos') {
      setCurrentStep('form');
    }
  };

  const handleComplete = async () => {
    try {
      const localId = nanoid();
      
      // Save to offline database
      await offlineDB.inspections.add({
        localId,
        jobId: selectedJob || '',
        leadId: selectedLead,
        formData,
        photos,
        createdAt: new Date().toISOString(),
        synced: false,
      });

      toast.success('Inspection saved! Will sync when online.');
      navigate('/');
    } catch (error) {
      console.error('Failed to save inspection:', error);
      toast.error('Failed to save inspection');
    }
  };

  return (
    <div className="min-h-screen bg-background pb-20">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b">
        <div className="flex items-center gap-3 p-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleBack}
            disabled={currentStep === 'select'}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="font-semibold">Quick Inspection</h1>
            <p className="text-sm text-muted-foreground">
              Step {currentStepIndex + 1} of {steps.length}
            </p>
          </div>
        </div>
        <Progress value={progress} className="h-1" />
      </div>

      {/* Step Content */}
      <div className="p-4">
        {currentStep === 'select' && (
          <JobLeadSelector
            selectedJob={selectedJob}
            selectedLead={selectedLead}
            onSelectJob={setSelectedJob}
            onSelectLead={setSelectedLead}
          />
        )}

        {currentStep === 'form' && (
          <QuickInspectionForm
            formData={formData}
            onChange={setFormData}
          />
        )}

        {currentStep === 'photos' && (
          <PhotoCaptureStep
            photos={photos}
            onPhotosChange={setPhotos}
          />
        )}
      </div>

      {/* Bottom Actions */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-background border-t">
        {currentStep === 'photos' ? (
          <Button
            onClick={handleComplete}
            className="w-full h-12"
            size="lg"
            disabled={photos.length === 0}
          >
            <Check className="w-5 h-5 mr-2" />
            Complete Inspection
          </Button>
        ) : (
          <Button
            onClick={handleNext}
            className="w-full h-12"
            size="lg"
          >
            Next Step
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        )}
      </div>
    </div>
  );
}
